<template>
  <div class="pl-detail">
    <div class="pl-detail__head">
      <p class="pl-detail__title">{{ headline }}</p>
      <p class="pl-detail__time">{{ timeRange }}</p>
      <p v-if="entry.messages.length" class="pl-detail__messages">
        <span
          v-for="(m, i) in entry.messages"
          :key="i"
          class="pl-detail__message"
          :class="'pl-detail__message--' + m.level"
        ><app-icon name="alertTriangle" :size="14" />{{ m.text }}</span>
      </p>
    </div>
    <section v-for="section in sections" :key="section.key" class="pl-detail__section">
      <h3 class="pl-detail__heading">{{ section.title }}</h3>
      <dl class="pl-detail__list">
        <template v-for="row in section.rows">
          <dt :key="'t-' + row.label" class="pl-detail__label">{{ row.label }}</dt>
          <dd :key="'d-' + row.label" class="pl-detail__value">
            <a
              v-if="row.href"
              class="pl-detail__link"
              :href="row.href"
              target="_blank"
              rel="noopener noreferrer"
            >{{ row.value }}<app-icon name="external" :size="12" /></a>
            <template v-else>{{ row.value }}</template>
          </dd>
        </template>
      </dl>
    </section>
    <!-- Verkäufe nur vereinfacht (Kundenwunsch 05.11.2026): alle Angaben zum Verkauf stehen unter Verkäufe -->
    <p v-if="entry.source === 'sale'" class="pl-detail__hint">
      <app-icon name="info" :size="14" />
      <span>Tarif, Zahlart, Strecke und Storno stehen unter
        <router-link to="/administration/ticketsales" class="pl-detail__link">Verwaltung → Verkäufe</router-link>.</span>
    </p>
  </div>
</template>

<script>
// Inhalt des Detailbereichs im Druckprotokoll (Verwaltung → Druckprotokoll, 05.11.2026): Art bzw. Bezeichnung, Zeit
// von–bis mit Dauer, Meldungen; Abschnitte Druck bzw. Verkauf (vereinfacht: Fahrkarte, Preis, Benutzer), Ort
// (Haltestelle, Linie, Fahrt, GPS mit Link auf OpenStreetMap), Rolle (Rollen, Abschnitte, Papierlänge), Gerät (Drucker
// mit Bemerkung, Druckertyp, Seriennummer, Gerät, App-Version, Druckerstatus) und Status (gemeldeter Fehlschlag des
// Papiereinzugs, unbekannte Papierposition, gespeichertes Diagnoseprotokoll). Zeilen und Abschnitte ohne Wert entfallen
// (ältere Einträge ohne Standort). entry: Zeile aus printLog.js (normalizeRow).
import moment from 'moment';
import { formatEuro, LEER } from '@/utils/format';
import { positionText } from './printLog';

function present(value) {
  return value !== null && value !== undefined && value !== '';
}

function filled(rows) {
  return rows.filter((row) => present(row.value) && row.value !== LEER);
}

function exact(text) {
  const m = present(text) ? moment(text) : null;
  return m && m.isValid() ? m.format('DD.MM.YYYY, HH:mm:ss') : null;
}

export default {
  name: 'PrintLogDetail',
  props: {
    entry: { type: Object, required: true },
    // angezeigter Benutzer (voller Name aus der Benutzerliste der Seite, sonst Login)
    userLabel: { type: String, default: null }
  },
  computed: {
    isSale() {
      return this.entry.source === 'sale';
    },
    headline() {
      return this.entry.description || this.entry.kind_label;
    },
    // „05.11.2026, 15:28:03 – 15:28:11 (8 s)“
    timeRange() {
      const start = present(this.entry.time) ? moment(this.entry.time) : null;
      if (!start || !start.isValid()) {
        return LEER;
      }
      const end = present(this.entry.end_time) ? moment(this.entry.end_time) : null;
      const text = start.format('DD.MM.YYYY, HH:mm:ss');
      if (!end || !end.isValid() || !end.isAfter(start)) {
        return text;
      }
      const seconds = end.diff(start, 'seconds');
      const endText = end.isSame(start, 'day') ? end.format('HH:mm:ss') : end.format('DD.MM.YYYY, HH:mm:ss');
      return text + ' – ' + endText + ' (' + (seconds < 120 ? seconds + ' s' : Math.round(seconds / 60) + ' Min.') + ')';
    },
    mainRows() {
      const e = this.entry;
      const rows = [{ label: 'Art', value: e.kind_label }];
      if (this.isSale) {
        rows.push({ label: 'Fahrkarte', value: e.description });
        rows.push({ label: 'Preis', value: e.price !== null ? formatEuro(e.price) : null });
      } else {
        rows.push({ label: 'Bezeichnung', value: e.description });
      }
      // Ende nur, wenn es vom Beginn abweicht (Meldungen tragen dieselbe Zeit); „success“ der App lesbar
      const message = present(e.print_message) && String(e.print_message).trim().toLowerCase() === 'success'
        ? 'erfolgreich' : e.print_message;
      rows.push(
        { label: 'Beginn', value: exact(e.time) },
        { label: 'Ende', value: e.end_time !== e.time ? exact(e.end_time) : null },
        { label: 'Benutzer', value: this.userLabel || e.username },
        { label: 'Druckmeldung', value: message }
      );
      return filled(rows);
    },
    placeRows() {
      const e = this.entry;
      return filled([
        { label: 'Haltestelle', value: e.stop_name },
        { label: 'Linie', value: present(e.line_id) && String(e.line_id) !== '0' ? String(e.line_id) : null },
        { label: 'Fahrt', value: present(e.trip_nr) && e.trip_nr !== '0' ? e.trip_nr : null },
        { label: 'GPS', value: positionText(e.gps_lat, e.gps_lon), href: e.osm_url }
      ]);
    },
    rollRows() {
      const e = this.entry;
      const groups = e.roll_groups;
      const sections = groups.reduce((n, g) => n + g.sections.length, 0);
      // Positionsmeldungen und Zustände: gelesene Stelle der Rolle, kein Verbrauch
      const label = e.group === 'state' ? 'Stelle' : (sections === 1 ? 'Abschnitt' : 'Abschnitte');
      return filled([
        { label: groups.length > 1 ? 'Rollen' : 'Rolle', value: groups.map((g) => g.roll).join(', ') },
        {
          label,
          value: groups.map((g) => (groups.length > 1 ? g.roll + ': ' : '') + g.sections.join(', ')).join('; ')
        },
        { label: 'Papierlänge', value: e.paper_text }
      ]);
    },
    deviceRows() {
      const e = this.entry;
      const printer = present(e.printer_id)
        ? String(e.printer_id) + (e.printer_comment ? ' (' + e.printer_comment + ')' : '')
        : null;
      return filled([
        { label: 'Drucker', value: printer },
        { label: 'Druckertyp', value: e.printer_type },
        { label: 'Drucker-Seriennr.', value: e.printer_serial },
        { label: 'Geräte-ID', value: e.device_id },
        { label: 'App-Version', value: e.app_version },
        { label: 'Druckerstatus', value: e.printer_status }
      ]);
    },
    statusRows() {
      const e = this.entry;
      return filled([
        { label: 'Papiereinzug', value: e.autoload_fail ? 'als fehlgeschlagen gemeldet' : null },
        { label: 'Papierposition', value: e.unknown_paper_position ? 'unbekannt' : null },
        {
          label: 'Diagnose',
          value: e.has_log ? 'Diagnoseprotokoll des Druckers gespeichert (Rollennummer bzw. Status)' : null
        }
      ]);
    },
    sections() {
      return [
        { key: 'main', title: this.isSale ? 'Verkauf' : 'Druck', rows: this.mainRows },
        { key: 'place', title: 'Ort', rows: this.placeRows },
        { key: 'roll', title: 'Rolle', rows: this.rollRows },
        { key: 'device', title: 'Gerät', rows: this.deviceRows },
        { key: 'status', title: 'Status', rows: this.statusRows }
      ].filter((section) => section.rows.length);
    }
  }
};
</script>

<style scoped>
.pl-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
.pl-detail__head {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
p.pl-detail__title {
  margin: 0;
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -.01em;
  color: var(--ink);
  overflow-wrap: anywhere;
}
p.pl-detail__time {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}
p.pl-detail__messages {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin: 4px 0 0;
}
.pl-detail__message {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
}
.pl-detail__message--error {
  color: var(--red);
}
.pl-detail__message--warn {
  color: var(--amber);
}
/* Abschnitt wie im Verkaufsdetail: Linie oben, Überschrift 12/16 600 --ink-3, darunter die Datenliste */
.pl-detail__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--line-2);
}
h3.pl-detail__heading {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: .02em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.pl-detail__list {
  display: grid;
  grid-template-columns: 124px minmax(0, 1fr);
  gap: 8px 12px;
  margin: 0;
}
.pl-detail__label {
  font-size: 12px;
  line-height: 20px;
  color: var(--ink-3);
}
.pl-detail__value {
  margin: 0;
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
a.pl-detail__link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--link);
  text-decoration: none;
}
:where(html:not(.dichte-touch)) a.pl-detail__link:hover {
  text-decoration: underline;
}
p.pl-detail__hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  border-radius: var(--r-ctl);
  background: var(--band);
  color: var(--ink-2);
  font-size: 12px;
  line-height: 18px;
}
p.pl-detail__hint .app-icon {
  flex: none;
  margin-top: 2px;
  color: var(--ink-3);
}
</style>
